import React, { useContext, useState } from "react";
import PropTypes from "prop-types";
import { Form, Formik } from "formik";
import { Button, ButtonLink, TextBox } from "../..";
import { GoogleLogin } from "react-google-login";
import FacebookLogin from "react-facebook-login";
import liff from "@line/liff";
import { toast } from "react-toastify";

// * ENV
const googleClientId = import.meta.env.GOOGLE_CLIENT_ID;
const lineClientId = import.meta.env.LINE_CLIENT_ID;
const facebookAppId = import.meta.env.FACEBOOK_APP_ID;

import { ReactComponent as GoogleSvg } from "../../../assets/icons/google.svg";
import { ReactComponent as FacebookSvg } from "../../../assets/icons/facebook.svg";
import { ReactComponent as LineSvg } from "../../../assets/icons/line.svg";
import { ReactComponent as WeChatSvg } from "../../../assets/icons/wechat.svg";
import { ReactComponent as ArrowRightSvg } from "../../../assets/icons/arrow-right.svg";
import { ReactComponent as ArrowRightDisabledSvg } from "../../../assets/icons/arrow-right-disabled.svg";
import { ReactComponent as EmailSvg } from "../../../assets/icons/email.svg";
import { ReactComponent as LockSvg } from "../../../assets/icons/lock.svg";

import useAuth from "../../../hooks/useAuth";
import SignUpContext from "./SignUpContext";
import { useNavigate } from "react-router-dom";
import useUsers from "../../../hooks/useUsers";
import AppContext from "../../../AppContext";
import { langComponent } from "../../../constants";

const SignUpForm = (props) => {
  liff.init({ liffId: lineClientId });
  const navigate = useNavigate();
  const { signInExternal } = useAuth();

  // * Hooks
  const { createRegistration, checkEmailExistence } = useAuth();
  const { queryUsers } = useUsers();

  const [toggleForgotPassword, setToggleForgotPassword] = useState(false);

  // * Context
  const { setRegistrationId, setCredentials } = useContext(SignUpContext);
  const { getLang } = useContext(AppContext);
  const Translation = getLang(langComponent.auth);

  // * Props
  const { onSwitch, onSignUp, onForgotPassword, onClose, onNavigateToOnboard } =
    props;

  // * Handle forgot password
  const handleForgotPassword = () => onForgotPassword();

  // * Handle switching signup/login
  const handleSwitch = () => onSwitch();

  // * Handle forgot password
  const handleSignUp = async (username, password) => {
    // * Create registration
    try {
      const registrationId = await createRegistration(username);
      setCredentials({ username, password });
      setRegistrationId(registrationId);
      onSignUp();
    } catch (error) {
      toast.error(`Unable to create registration`);
    }
  };

  const getPasswordStrength = (password) => {
    // * Initialize letiables
    let strength = 0;

    // * Check password length
    if (password.length >= 8) {
      strength += 1;
    }

    // * Check for mixed case
    if (password.match(/[a-z]/) && password.match(/[A-Z]/)) {
      strength += 1;
    }

    // * Check for numbers
    if (password.match(/\d/)) {
      strength += 1;
    }

    // * Check for special characters
    if (password.match(/[^a-zA-Z\d]/)) {
      strength += 1;
    }

    // * Return results
    if (strength < 2) {
      return { value: 1, tip: Translation("Easy") };
    } else if (strength === 2) {
      return { value: 2, tip: Translation("Medium") };
    } else if (strength === 3) {
      return { value: 3, tip: Translation("Great") };
    } else {
      return { value: 4, tip: Translation("Extreme") };
    }
  };

  // * ====== Social Login Handling ======
  const handleGoogleSignInSuccess = async (e) => {
    try {
      const { googleId, profileObj } = e;

      const findUser = await queryUsers({
        googleId: googleId,
      });

      if (findUser.data.length === 0) {
        localStorage.setItem(
          "onboard:credentials",
          JSON.stringify({
            googleId: googleId,
            googleAvatar: profileObj.imageUrl,
          })
        );
        localStorage.setItem("onboard:registration-id", "google");
        onClose();
        navigate("onboard");
        return;
      }

      const token = await signInExternal(googleId, "google");
      setToken(token);
      handleClose();
    } catch (error) {
      toast.error(`Unable to sign in google`);
    }
  };

  const isFacebookApp = () => {
    const ua = navigator.userAgent || navigator.vendor || window.opera;
    return (
      ua.indexOf("FBAN") > -1 ||
      ua.indexOf("FBAV") > -1 ||
      ua.indexOf("Instagram") > -1
    );
  };

  const handleGoogleSignInFailure = (e) => {
    console.error(e);
  };

  const handleFacebookResponse = async (e) => {
    try {
      const { userID, picture } = e;
      // TODO image from facebook can't use error 404
      // * picture.data.url
      const findUser = await queryUsers({
        facebookId: userID,
      });

      if (findUser.data.length === 0) {
        localStorage.setItem(
          "onboard:credentials",
          JSON.stringify({
            facebookId: userID,
            facebookAvatar: picture?.data?.url ?? undefined,
          })
        );
        localStorage.setItem("onboard:registration-id", "facebook");
        return onNavigateToOnboard("/onboard");
      }

      const token = await signInExternal(userID, "facebook");
      setToken(token);
      handleClose();
    } catch (error) {
      console.log("error", error);
      toast.error("Unable to sign in by facebook");
    }
  };

  const handleLineRequest = () => {
    liff.login();
  };

  return (
    <>
      <h1 className="login-header">{Translation("Sign Up")}</h1>
      <Formik
        initialValues={{
          username: "",
          password: "",
          confirmPassword: "",
        }}
        isInitialValid={false}
        validate={({ username, password, confirmPassword }) => {
          setToggleForgotPassword(false);

          const errors = {};

          if (!username) {
            errors.username = Translation("Username cannot be blank");
          }

          // * Check if email is valid
          const validEmaiLRegex =
            /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
          if (!username.match(validEmaiLRegex)) {
            errors.username = Translation("Invalid email");
          }

          if (!password) {
            errors.password = Translation("Password cannot be blank");
          }

          if (!confirmPassword) {
            errors.confirmPassword = Translation("Password cannot be blank");
          }

          if (password && confirmPassword && password !== confirmPassword) {
            errors.password = Translation("Password don't match");
            errors.confirmPassword = Translation("Password don't match");
          }

          return errors;
        }}
        onSubmit={(values, { setFieldError }) => {
          // TODO: Check if email is already registered
          const { username, password } = values;
          checkEmailExistence(username).then((result) => {
            if (result.status) {
              setFieldError("username", Translation("Account already exists"));
              setToggleForgotPassword(true);
              return;
            }

            handleSignUp(username, password);
          });
        }}
      >
        {({ values: { username, password, confirmPassword }, isValid }) => {
          const passwordStrength = getPasswordStrength(password);

          return (
            <Form className="login-form">
              <TextBox
                name="username"
                placeholder={Translation("Email")}
                type="text"
                leadingIcon={<EmailSvg />}
                values={username}
                error={
                  toggleForgotPassword && (
                    <span>
                      {Translation("Account already exists")},{" "}
                      <span
                        style={{
                          cursor: "pointer",
                          textDecoration: "underline",
                        }}
                        onClick={handleForgotPassword}
                      >
                        {Translation("Forgot Password")}?
                      </span>
                    </span>
                  )
                }
              />
              <TextBox
                name="password"
                placeholder={Translation("Password")}
                type="password"
                leadingIcon={<LockSvg />}
                values={confirmPassword}
              />
              <TextBox
                name="confirmPassword"
                placeholder={Translation("Confirm Password")}
                type="password"
                leadingIcon={<LockSvg />}
                values={password}
              />
              {password && (
                <div className="signup-form__password-strength">
                  <div
                    className={`signup-form__password-strength-bar signup-form__password-strength-bar--${passwordStrength.value}`}
                  >
                    <div
                      className="signup-form__password-strength-bar__progress"
                      style={{ width: `${passwordStrength.value * 25}%` }}
                    />
                  </div>
                  <div
                    className={`signup-form__password-strength-desc signup-form__password-strength-desc--${passwordStrength.value}`}
                  >
                    {passwordStrength.tip}
                  </div>
                </div>
              )}
              <Button
                type="submit"
                label={Translation("SIGN UP")}
                trailingIcon={
                  isValid ? <ArrowRightSvg /> : <ArrowRightDisabledSvg />
                }
                style={{ margin: "28px 0 40px", width: "300px" }}
                disabled={!isValid}
              />
            </Form>
          );
        }}
      </Formik>
      <span>{Translation("Or sign up with")}</span>
      <div className="login__socials">
        <div className="login__socials-link login__socials-link__google">
          <GoogleLogin
            clientId={googleClientId}
            onSuccess={handleGoogleSignInSuccess}
            onFailure={handleGoogleSignInFailure}
            cookiePolicy={"single_host_origin"}
            render={(renderProps) => (
              <GoogleSvg onClick={renderProps.onClick} />
            )}
          />
        </div>
        <div className="login__socials-link login__socials-link__facebook">
          <FacebookLogin
            appId={facebookAppId}
            autoLoad={false}
            fields="name,email,picture.width(500).height(500)"
            callback={handleFacebookResponse}
            cssClass="login__socials-link login__socials-link__facebook"
            size="small"
            textButton=""
            icon={<FacebookSvg />}
            disableMobileRedirect={!isFacebookApp()}
            redirectUri={"https://urbaancommunities.com/facebook"}
          />
        </div>
        <div className="login__socials-link login__socials-link__line">
          <LineSvg onClick={handleLineRequest} />
        </div>
      </div>
      <div className="login__switch">
        <span>{Translation("Already have an account?")}</span>
        <ButtonLink content={Translation("Login")} onClick={handleSwitch} />
      </div>
    </>
  );
};

SignUpForm.propTypes = {
  onClose: PropTypes.func,
  onSwitch: PropTypes.func,
  onForgotPassword: PropTypes.func,
  onSignUp: PropTypes.func,
};

SignUpForm.defaultProps = {
  onClose: () => {},
  onSwitch: () => {},
  onForgotPassword: () => {},
  onSignUp: () => {},
};

export default SignUpForm;
